import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ResourceType } from 'src/app/common/enumerations/enumerations';
import { RadMonitor } from 'src/app/components/catalogs/rad-monitor/rad-monitor/rad-monitor';
import { Resource, ResourceSummary } from 'src/app/components/catalogs/beamline-catalog/resource/resources';
import { ResourcesService } from 'src/app/components/catalogs/beamline-catalog/resource/resources.service';
import { RadLocation } from 'src/app/components/catalogs/rad-monitor/rad-monitor/rad-location';
import { Placeholder } from 'src/app/components/checklists/checklists';
import { Store } from '@ngxs/store';

@Injectable({
  providedIn: 'root'
})
export class PlaceholdersService {
  BASE_URL = environment.urls.baseApiUrl; // Api URL
  Api = '/Placeholders';

  public isBuilder?: boolean;

  keyValuePairs: any[] = [];
  keyValuePairsObservable = new BehaviorSubject<any[]>(this.keyValuePairs);
  recalculateRadMonitorConditions = false;

  private placeholderRequestId = 0;

  placeholders?: Placeholder[];
  placeholdersFiltered?: Placeholder[];
  placeholders$!: Observable<Placeholder[]>;
  placeholdersSubs!: Subscription;

  rmco!: RadMonitor;
  rmci!: RadMonitor;

  constructor(
    private http: HttpClient,
    public resourceService: ResourcesService,
    private store: Store
  ) {
    this.loadPlaceholders();
  }

  getAll(): Observable<Placeholder[]> {
    const httpresult = this.http.get<Placeholder[]>(this.BASE_URL + this.Api);
    httpresult.toPromise().then(data => this.placeholders = data);
    return httpresult;
  }

  get(id: number): Observable<Placeholder> {
    const httpresult = this.http.get<Placeholder>(this.BASE_URL + this.Api + '/' + id);
    return httpresult;
  }

  create(placeholder: Placeholder): Observable<Placeholder> {
    return this.http.put<Placeholder>(this.BASE_URL + this.Api, placeholder);
  }

  update(placeholder: Placeholder): Observable<Placeholder> {
    return this.http.put<Placeholder>(this.BASE_URL + this.Api, placeholder);
  }

  delete(placeholder: Placeholder, text: string): Observable<Placeholder> {
    return this.http.delete<any>(this.BASE_URL + this.Api + '/' + placeholder.id + '/' + text);
  }

  getValue(placeholder: string) {
    if (this.keyValuePairs) {
      const object = this.placeholders?.find(x => x.placeholderString.toLowerCase() == placeholder?.toLowerCase())?.object;
      if (object) {
        const obj = object?.split('|');
        let value = this.keyValuePairs.find(x => x.key.toLowerCase() == obj[0]?.toLowerCase())?.value;
        value = (obj && obj[1]) ? (value ? value[(obj[1])] : 'No value Set!') : value;
        if (typeof value === 'object') {
          if (Array.isArray(value)) {
            value = value.map(x => x.Shutter.name).join(', ');
          }
        }
        if (!this.isBuilder && !value) {
          return `Value for ${placeholder} not found!`;
        } else {
          return value;
        }
      }
      else {
        return 'No Value Set!';
      }
    }
    else {
      return 'Wrong Placeholder Found: <b class="red">' + placeholder + '</b>';
    }
  }

  loadPlaceholders() {
    this.placeholders$ = this.store.select(state => state.Placeholders.data);
    this.placeholdersSubs = this.placeholders$.subscribe(data => {
      if (data?.length) {
        this.placeholders = data;
      }
    });
  }

  /**
   * Loads placeholder values for the selected catalog resource.
   * Supports resource payloads that expose their type through either type or resourceTypeID.
   * @param resource Resource used to populate placeholder values.
   * @param isBuilder Whether unresolved values are being displayed in a builder.
   * @returns True after the current placeholder request has been processed.
   */
  async setKeyValuePairs(resource?: Resource | RadMonitor | null, isBuilder?: boolean): Promise<boolean> {
    if (!this.placeholders?.length)
      this.loadPlaceholders();

    const requestId = ++this.placeholderRequestId;
    this.isBuilder = isBuilder;

    if (!resource) {
      this.clearkeyValues();
      return true;
    }

    // Builder payloads may be slim, so use the full store resource to recover missing type information.
    const storedResource = resource.id
      ? this.store.selectSnapshot((state) => state.Resources.data)?.find((item: Resource) => item.id == resource.id)
      : null;
    const resourceType = resource.type
      ?? (resource as Resource).resourceTypeID
      ?? storedResource?.type
      ?? storedResource?.resourceTypeID;
    if (resource.id && resourceType == ResourceType.Shutter) {
      const resourceSummary = await this.resourceService.getResourceSummaryByShutter(resource.id).toPromise();
      if (requestId == this.placeholderRequestId && resourceSummary)
        this.getKeyValues(resourceSummary);
      return true;
    }

    if (resource.id && resourceType == ResourceType.Beamline) {
      const resourceSummary = await this.resourceService.getResourceSummaryByBeamline(resource.id).toPromise();
      if (requestId == this.placeholderRequestId && resourceSummary)
        this.getKeyValues(resourceSummary);
      return true;
    }

    // Non-catalog resources use the Rad Monitor placeholder namespace.
    if (requestId == this.placeholderRequestId && (resourceType == ResourceType.RadMonitor || 'radLocation' in resource)) {
      const radMonitor = resource as RadMonitor;
      this.getKeyValuesRadMonitor(radMonitor, 'rmco');
      if (radMonitor.radLocation)
        this.getKeyValuesRadMonitor(radMonitor.radLocation as RadLocation, 'radLocation');
    }
    return true;
  }

  /**
   * Builds the placeholder lookup values from a beamline resource summary.
   * The FVS magnet assembly placeholder uses the combined KE text from the summary's fast valve sensors.
   * @param rs Resource summary used to populate shutter and beamline placeholders.
   */
  getKeyValues(rs: ResourceSummary) {
    const keyValues: any[] = [];
    if (rs.shutter != undefined) {
      const shutter = { ...rs.shutter };
      const fvsMagnetAssembly = rs.fastValveSensors
        ?.map(resource => resource.keText)
        .filter((value): value is string => !!value?.trim())
        .join(' ');

      // Resource summaries expose FVS items separately, while the placeholder expects shutter.KEText.
      if (fvsMagnetAssembly)
        shutter.keText = fvsMagnetAssembly;

      keyValues.push.apply(keyValues, Object.entries(shutter).map(([key, value]) => ({ key: 'shutter.' + key, value })));
    }
    if (rs.beamline)
      keyValues.push.apply(keyValues, Object.entries(rs.beamline).map(([key, value]) => ({ key: 'beamline.' + key, value })));
    this.keyValuePairs = keyValues;
    this.keyValuePairsObservable.next(this.keyValuePairs);
  }

  /**
   * Publishes Rad Monitor placeholder values and identifies explicit outgoing-location changes.
   * @param radItem - Rad Monitor or location supplying placeholder values.
   * @param type - Prefix used by the configured placeholders.
   * @param recalculateConditions - Whether dependent conditions should evaluate the published values.
   */
  getKeyValuesRadMonitor(radItem: RadMonitor | RadLocation, type: string, recalculateConditions = false) {
    const keyValues: any[] = [];
    if (radItem) {
      keyValues.push.apply(keyValues, Object.entries(radItem).map(([key, value]) => ({ key: type + '.' + key, value })));
    }
    // Dependent conditions recalculate only when the outgoing-monitor step is completed.
    this.recalculateRadMonitorConditions = recalculateConditions;
    this.keyValuePairs = keyValues;
    this.keyValuePairsObservable.next(this.keyValuePairs);
    this.recalculateRadMonitorConditions = false;
  }

  clearkeyValues() {
    this.keyValuePairs = [];
    this.keyValuePairsObservable.next(this.keyValuePairs);
  }
}
